import type { RacePageDTO } from '@mobi/api-types'
import type { RaceListResponse } from '@core/Areas/Racing/Hooks/useRaceList/types'
import type { RaceCodes, RaceCodesLowerCase } from '@core/Areas/Racing/Types/MeetingInformation'
import { useAppSeo } from '../useAppSeo'

type RaceSeoInput =
  | { page: 'meetings'; data: { raceCode?: RaceCodesLowerCase } }
  | { page: 'race-card'; data: { race?: RacePageDTO } }
  | { page: 'race-list'; data: { meeting?: RaceListResponse } }

/**
 * Racing SEO component: maps racing domain inputs to generic app SEO.
 */
export const useAppSeoForRacing = ({ page, data }: RaceSeoInput) => {
  let title = 'Racing Betting | TABtouch'
  let description =
    'Explore racing odds, form guides, expert tips and betting markets with TABtouch.'
  const canonicalHref = '/tote'

  if (page === 'meetings') {
    const { raceCode } = data
    const racingType = getRacingType(raceCode)
    title = raceCode ? `${racingType} Racing Betting | TABtouch` : title
    description = raceCode
      ? `Explore ${racingType.toLowerCase()} racing odds, form guides, expert tips and betting markets for Australian and international thoroughbred racing.`
      : description
  }

  if (page === 'race-card') {
    const { race } = data
    if (race) {
      const { MeetingName: track, SelectedRace, MeetingCodeText } = race.MeetingInformation
      const raceNumber = SelectedRace.RaceNumber
      const racingType = getRacingType(MeetingCodeText)
      title = getRaceCardTitle(track, raceNumber, MeetingCodeText)
      description = `Bet on ${racingType.toLowerCase()} racing at ${track}. Compare odds, runners and form guides for Race ${raceNumber} with TABtouch.`
    }
  }

  if (page === 'race-list') {
    const { meeting } = data
    if (meeting) {
      const { courseName: track, meetingCode } = meeting
      title = getRaceListTitle(track, meetingCode)
      description = getRaceListDescription(track, meetingCode)
    }
  }

  useAppSeo({ title, description, canonicalHref })
}

// =============
// Local Helpers
// =============

function getRacingType(code?: string): string {
  switch (code?.toLowerCase()) {
    case 'dogs':
      return 'Greyhound'
    case 'trots':
      return 'Harness'
    default:
      return 'Horse'
  }
}

function getRaceCardTitle(track: string, raceNumber: number, meetingCode: RaceCodes): string {
  switch (meetingCode) {
    case 'Dogs':
      return `${track} R${raceNumber} Greyhound Race | TABtouch`
    case 'Trots':
      return `${track} R${raceNumber} Harness Race | TABtouch`
    default:
      return `${track} R${raceNumber} | TABtouch`
  }
}

function getRaceListTitle(track: string, meetingCode: RaceCodes): string {
  switch (meetingCode) {
    case 'Dogs':
      return `${track} Greyhound Races | TABtouch`
    case 'Trots':
      return `${track} Trots Races | TABtouch`
    default:
      return `${track || ''} Races Today | TABtouch`
  }
}

function getRaceListDescription(track: string, meetingCode: RaceCodes): string {
  switch (meetingCode) {
    case 'Dogs':
      return `View greyhound racing at ${track}, including race fields, odds and betting markets with TABtouch.`
    case 'Trots':
      return `View harness racing at ${track}, including race fields, odds and betting markets with TABtouch.`
    default:
      return `View today's races at ${track}, including race fields, odds and betting markets with TABtouch.`
  }
}
